/* eslint-disable react/no-unescaped-entities */
/**
 * Register — iter71 (iter87.99 — Premium tier removed from the picker)
 *
 * Dedicated registration page for new Rivalry admins. Replaces the
 * "Request Rivalry" form on the Landing card and offers a tier picker:
 *
 *   • Free   — Default. 1 active Tournament at a time.
 *   • Trial  — Up to 5 active Tournaments. Requires a trial code.
 *
 * Premium is no longer a self-serve request tier; tenants who go paid
 * are elevated via Site Admin (Comp → Trial → Pro+ bump / Clerk Pro+
 * sign-up).
 *
 * The submitted tier is the user's *preference*; the site admin makes
 * the final call at approval time (override possible).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { isTurnstileReady } from "@/components/TurnstileProvider";
import { ArrowLeft, Sparkles, Trophy, Info, Lock, Tag, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import TermsModal from "@/components/TermsModal";
import PrivacyModal from "@/components/PrivacyModal";
import { TERMS_VERSION } from "@/lib/terms";

// Per-tier metadata. Ordered Free → Trial for visual hierarchy.
// iter87.99 — Premium tier removed from the request-a-Rivalry picker.
// Tenants who go paid are elevated via Site Admin (Comp → Trial → Pro+
// bump / Clerk Pro+ sign-up), not via a self-serve Premium request.
const TIERS = [
    {
        key: "free",
        label: "Free",
        icon: Trophy,
        color: "text-cyber",
        border: "border-cyber",
        bg: "bg-cyber/10",
        tagline: "Run 1 Tournament at a time",
        description:
            "Perfect for a casual crew running one tournament at a time (a World Cup, a March Madness bracket). Wrap one up before starting another.",
        bullets: ["1 active Tournament", "Unlimited players (up to 20)", "Forever-free Rivalry", "All scoring + bracket features"],
    },
    {
        key: "trial",
        label: "Trial",
        icon: Zap,
        color: "text-volt",
        border: "border-volt",
        bg: "bg-volt/10",
        tagline: "Up to 5 Tournaments, trial code required",
        description:
            "Get the full multi-Tournament experience to evaluate before going Pro+. Trial codes come from partners, promos, or the Tiny Crew team — paste yours below.",
        bullets: ["Up to 5 active Tournaments", "Full Season Standings", "Smack Stream + Hall of Burn", "Trial code required"],
    },
];

export default function Register() {
    const [requestsOpen, setRequestsOpen] = useState(true);
    // iter86.35 — Gate rendering on the settings fetch: rendering the form
    // optimistically caused a visible flash before flipping to "Requests
    // paused" when intake is off.
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    // iter72 — Per-tier acceptance + billing_enabled gate from the public
    // site-settings endpoint. Default to permissive while loading so a slow
    // settings fetch doesn't block the form. Hard-gate after load.
    const [tierOpen, setTierOpen] = useState({ free: true, trial: true });
    const [requestSent, setRequestSent] = useState(false);

    // Form fields
    const [leagueName, setLeagueName] = useState("");
    const [adminName, setAdminName] = useState("");
    const [adminEmail, setAdminEmail] = useState("");
    const [adminIsPlayer, setAdminIsPlayer] = useState(true);
    const [tier, setTier] = useState("free");
    const [trialCode, setTrialCode] = useState("");
    const [termsAccepted, setTermsAccepted] = useState(false);

    // UI state
    const [loading, setLoading] = useState(false);
    const [tierInfoFor, setTierInfoFor] = useState(null);
    const [termsModalOpen, setTermsModalOpen] = useState(false);
    const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
    // iter87.102 — Distinguish auto-approve (Turnstile happy path) vs
    // pending (manual queue) on the success screen.
    const [autoApproved, setAutoApproved] = useState(false);

    useEffect(() => {
        api.getSiteSettings()
            .then((r) => {
                setRequestsOpen(r?.league_requests_open !== false);
                if (r?.tier_requests_open) setTierOpen(r.tier_requests_open);
            })
            .catch(() => {})
            .finally(() => setSettingsLoaded(true));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!leagueName.trim() || !adminName.trim() || !adminEmail.trim()) {
            toast.error("Rivalry name, username, and email are all required");
            return;
        }
        if (!termsAccepted) {
            toast.error("Please read & accept the Terms of Use first");
            return;
        }
        if (tier === "trial" && !trialCode.trim()) {
            toast.error("Trial tier requires a trial code");
            return;
        }
        setLoading(true);
        // iter87.101 — Wait up to 3s for the Cloudflare Turnstile widget
        // to have a fresh token in the bag before firing the POST. The
        // axios interceptor reads the token synchronously — if the widget
        // is still minting (typical 1-2s on first page load), the request
        // goes out with NO cf-turnstile-response header and the backend
        // falls back to manual-queue mode instead of the auto-approve
        // fast path. We poll `isTurnstileReady()` without consuming; the
        // interceptor still burns the token on the outbound POST.
        const start = Date.now();
        while (!isTurnstileReady() && Date.now() - start < 3000) {
            await new Promise((r) => setTimeout(r, 100));
        }
        try {
            const res = await api.submitLeagueRequest({
                league_name: leagueName.trim(),
                admin_username: adminName.trim(),
                admin_email: adminEmail.trim().toLowerCase(),
                admin_is_player: adminIsPlayer,
                terms_accepted: true,
                terms_version: TERMS_VERSION,
                requested_tier: tier,
                trial_code: tier === "trial" ? trialCode.trim().toUpperCase() : null,
            });
            const wasAutoApproved = !!res?.auto_approved;
            setAutoApproved(wasAutoApproved);
            setRequestSent(true);
            toast.success(
                wasAutoApproved
                    ? "You're approved! Check your inbox for the setup link."
                    : "Request submitted — we'll email you when approved"
            );
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to submit request");
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-ink text-white relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-cyber/10 blur-3xl rounded-full" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-volt/10 blur-3xl rounded-full" />

            <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-6">
                <Link to="/" className="brutal-btn-ghost text-sm" data-testid="register-back-btn">
                    <ArrowLeft className="w-4 h-4" /> Back
                </Link>
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 bg-cyber border-2 border-black shadow-brutal flex items-center justify-center">
                        <Trophy className="w-5 h-5 text-black" strokeWidth={2.5} />
                    </div>
                    <span className="font-display font-black text-xl tracking-tight">TINY CREW RIVALRIES</span>
                </div>
            </header>

            <main className="relative z-10 px-6 lg:px-12 pb-20 max-w-3xl mx-auto" data-testid="register-page">
                <div className="space-y-2 mb-6">
                    <div className="inline-flex items-center gap-2 pill bg-ink text-cyber border-cyber">
                        <Sparkles className="w-3.5 h-3.5" /> Signup
                    </div>
                    <h1 className="font-display font-black text-4xl sm:text-5xl leading-[0.95]">
                        START YOUR <span className="text-cyber">TINY CREW RIVALRY</span>
                    </h1>
                    <p className="text-zinc-400 text-base">
                        Your Rivalry is your group's permanent home. Tell us about it — we'll review and email you a one-click setup link.
                    </p>
                </div>

                {!settingsLoaded && (
                    <div data-testid="register-loading-state" className="brutal-card p-6 text-center">
                        <p className="text-sm text-zinc-500">Checking availability…</p>
                    </div>
                )}

                {settingsLoaded && !requestsOpen && (
                    <div data-testid="requests-closed-state" className="brutal-card p-6 text-center space-y-3">
                        <Lock className="w-10 h-10 mx-auto text-blaze" />
                        <h3 className="font-display font-black text-xl">Requests paused</h3>
                        <p className="text-sm text-zinc-400 leading-relaxed">
                            We're not accepting new Rivalry requests right now. Check back soon.
                        </p>
                    </div>
                )}

                {settingsLoaded && requestsOpen && requestSent && (
                    <div data-testid="register-success-state" className="brutal-card p-6 text-center space-y-3">
                        <div className={`w-12 h-12 mx-auto ${autoApproved ? "bg-green-400" : "bg-cyber"} border-2 border-black shadow-brutal flex items-center justify-center`}>
                            <Sparkles className="w-6 h-6 text-black" strokeWidth={2.5} />
                        </div>
                        <h3 className="font-display font-black text-xl" data-testid="register-success-heading">
                            {autoApproved ? "You're approved!" : "Request submitted"}
                        </h3>
                        <p className="text-sm text-zinc-400 leading-relaxed" data-testid="register-success-copy">
                            {autoApproved ? (
                                <>
                                    Check <span className="text-green-400 font-bold">{adminEmail}</span> — your one-click setup link is on its way. Approved via Cloudflare Turnstile in real time.
                                </>
                            ) : (
                                <>
                                    We'll email <span className="text-cyber font-bold">{adminEmail}</span> with a one-click link as soon as your Rivalry is approved.
                                </>
                            )}
                        </p>
                        <Link to="/" className="brutal-btn-ghost text-sm inline-flex">
                            <ArrowLeft className="w-3 h-3" /> Back to home
                        </Link>
                    </div>
                )}

                {settingsLoaded && requestsOpen && !requestSent && (
                    <form onSubmit={handleSubmit} className="brutal-card p-6 lg:p-8 space-y-5">
                        {/* Rivalry basics */}
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                                Rivalry name
                            </label>
                            <input
                                data-testid="register-league-name-input"
                                type="text"
                                value={leagueName}
                                onChange={(e) => setLeagueName(e.target.value)}
                                placeholder="The Sunday Crew"
                                className="brutal-input w-full"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                                Your username (admin) <span className="text-blaze normal-case font-medium tracking-normal">— case-sensitive</span>
                            </label>
                            <input
                                data-testid="register-admin-name-input"
                                type="text"
                                value={adminName}
                                onChange={(e) => setAdminName(e.target.value)}
                                placeholder="captain_dave"
                                className="brutal-input w-full"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                                Email <span className="text-heat normal-case font-medium tracking-normal">— we'll send your approval link here</span>
                            </label>
                            <input
                                data-testid="register-admin-email-input"
                                type="email"
                                value={adminEmail}
                                onChange={(e) => setAdminEmail(e.target.value)}
                                placeholder="you@email.com"
                                className="brutal-input w-full"
                                required
                            />
                        </div>

                        <label className="flex items-start gap-3 cursor-pointer select-none brutal-card-light p-3" data-testid="register-admin-is-player">
                            <input
                                type="checkbox"
                                checked={adminIsPlayer}
                                onChange={(e) => setAdminIsPlayer(e.target.checked)}
                                className="mt-1 w-4 h-4 accent-cyber cursor-pointer"
                            />
                            <span className="text-xs text-zinc-300 leading-relaxed">
                                <span className="font-bold text-white">I'm playing in this Rivalry too.</span>
                                <br />
                                <span className="text-zinc-500">Uncheck if you just want to run the Rivalry (you'll still control the draft, scoring, etc. but won't be drafted teams).</span>
                            </span>
                        </label>

                        {/* Tier picker */}
                        <div className="space-y-2 border-t border-[#222] pt-4">
                            <div className="flex items-center justify-between">
                                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500">
                                    Rivalry tier
                                </label>
                                <span className="text-[10px] text-zinc-600">Site admin reviews & may adjust at approval</span>
                            </div>
                            <div className="grid sm:grid-cols-3 gap-2" data-testid="register-tier-picker">
                                {TIERS.map((t) => {
                                    const Icon = t.icon;
                                    const selected = tier === t.key;
                                    // iter72 — Hard-gate per tier:
                                    //   • tier_requests_open[t.key] === false  → site admin paused this tier
                                    //   • t.disabled                           → static "Soon" placeholder
                                    // (iter87.99 — Premium tier removed from the picker;
                                    // billing gating no longer applies here.)
                                    const closedByAdmin = tierOpen[t.key] === false;
                                    const tierDisabled = t.disabled || closedByAdmin;
                                    return (
                                        <div
                                            key={t.key}
                                            role="button"
                                            tabIndex={tierDisabled ? -1 : 0}
                                            onClick={() => !tierDisabled && setTier(t.key)}
                                            onKeyDown={(e) => {
                                                if (tierDisabled) return;
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    setTier(t.key);
                                                }
                                            }}
                                            aria-pressed={selected}
                                            aria-disabled={tierDisabled || undefined}
                                            data-testid={`register-tier-${t.key}`}
                                            title={
                                                closedByAdmin
                                                    ? `${t.label} signups are paused right now`
                                                    : undefined
                                            }
                                            className={`brutal-card-light text-left p-3 transition-all relative outline-none ${
                                                selected ? `${t.bg} ${t.border}` : "border-[#333] hover:border-zinc-500"
                                            } ${tierDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer focus-visible:ring-2 focus-visible:ring-cyber"}`}
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <Icon className={`w-4 h-4 ${t.color}`} strokeWidth={2.5} />
                                                {/* iter71 — Info trigger is a <span role="button"> rather
                                                    than <button> to avoid the nested-button HTML invalid
                                                    structure (parent tier-card is also a clickable div). */}
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={(e) => { e.stopPropagation(); setTierInfoFor(t.key); }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            setTierInfoFor(t.key);
                                                        }
                                                    }}
                                                    aria-label={`More info about ${t.label} tier`}
                                                    data-testid={`register-tier-info-${t.key}`}
                                                    className="text-zinc-500 hover:text-cyber cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-cyber"
                                                >
                                                    <Info className="w-3.5 h-3.5" />
                                                </span>
                                            </div>
                                            <div className={`font-display font-extrabold text-sm uppercase ${selected ? t.color : "text-white"}`}>
                                                {t.label}
                                            </div>
                                            <div className="text-[10px] text-zinc-500 mt-1 leading-tight">
                                                {closedByAdmin ? "Paused — check back soon" : t.tagline}
                                            </div>
                                            {t.disabled && !closedByAdmin && (
                                                <span className="absolute top-2 right-8 pill text-[8px] uppercase font-bold text-neon border-neon">
                                                    Soon
                                                </span>
                                            )}
                                            {closedByAdmin && (
                                                <span
                                                    data-testid={`register-tier-paused-${t.key}`}
                                                    className="absolute top-2 right-8 pill text-[8px] uppercase font-bold text-heat border-heat"
                                                >
                                                    Paused
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {tier === "trial" && (
                                <div className="space-y-2 pt-3" data-testid="register-trial-code-block">
                                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500">
                                        Trial code <span className="text-heat normal-case font-medium tracking-normal">— required for Trial tier</span>
                                    </label>
                                    <div className="relative">
                                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-volt" />
                                        <input
                                            data-testid="register-trial-code-input"
                                            type="text"
                                            value={trialCode}
                                            onChange={(e) => setTrialCode(e.target.value.toUpperCase())}
                                            placeholder="TRIAL2026"
                                            className="brutal-input w-full pl-10 font-mono uppercase"
                                            required
                                        />
                                    </div>
                                    <p className="text-[11px] text-zinc-500">
                                        Don't have a code? Reach out to the Tiny Crew team — partners + verified communities are eligible.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* T&C */}
                        <label
                            className={`flex items-start gap-3 cursor-pointer select-none brutal-card-light p-3 ${termsAccepted ? "border-cyber/40" : "border-heat/40"}`}
                            data-testid="register-terms-block"
                        >
                            <input
                                data-testid="register-terms-checkbox"
                                type="checkbox"
                                checked={termsAccepted}
                                onChange={(e) => {
                                    if (e.target.checked && !termsAccepted) setTermsModalOpen(true);
                                    else setTermsAccepted(false);
                                }}
                                className="mt-1 w-4 h-4 accent-cyber cursor-pointer"
                            />
                            <span className="text-xs text-zinc-300 leading-relaxed">
                                <span className="font-bold text-white">I agree to the Terms of Use.</span>
                                <br />
                                <span className="text-zinc-500">
                                    Entertainment only — no money exchanges via the Platform. Results & calculations not guaranteed.{" "}
                                    <button
                                        type="button"
                                        onClick={(e) => { e.preventDefault(); setTermsModalOpen(true); }}
                                        className="text-cyber underline hover:text-cyber/80"
                                    >
                                        Read full terms
                                    </button>
                                    {" · "}
                                    <button
                                        type="button"
                                        onClick={(e) => { e.preventDefault(); setPrivacyModalOpen(true); }}
                                        className="text-cyber underline hover:text-cyber/80"
                                    >
                                        Privacy notice
                                    </button>
                                </span>
                            </span>
                        </label>

                        <button
                            data-testid="register-submit-btn"
                            type="submit"
                            disabled={loading || !termsAccepted}
                            className="brutal-btn-primary w-full text-base disabled:opacity-60"
                        >
                            {loading ? "Submitting..." : "Submit signup request"}
                        </button>
                    </form>
                )}
            </main>

            {/* Tier info modal */}
            {tierInfoFor && (
                <TierInfoModal
                    tier={TIERS.find((t) => t.key === tierInfoFor)}
                    onClose={() => setTierInfoFor(null)}
                />
            )}

            <TermsModal
                open={termsModalOpen}
                onClose={() => setTermsModalOpen(false)}
                onAccept={() => { setTermsAccepted(true); setTermsModalOpen(false); }}
            />
            <PrivacyModal open={privacyModalOpen} onClose={() => setPrivacyModalOpen(false)} />
        </div>
    );
}

function TierInfoModal({ tier, onClose }) {
    if (!tier) return null;
    const Icon = tier.icon;
    return (
        <div
            data-testid="tier-info-modal"
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="brutal-card p-6 max-w-md w-full space-y-4"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-3">
                    <Icon className={`w-6 h-6 ${tier.color}`} strokeWidth={2.5} />
                    <h3 className={`font-display font-black text-2xl uppercase ${tier.color}`}>{tier.label}</h3>
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed">{tier.description}</p>
                <ul className="space-y-1 text-xs text-zinc-400">
                    {tier.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2">
                            <span className="text-cyber mt-0.5">▸</span> {b}
                        </li>
                    ))}
                </ul>
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="tier-info-close"
                    className="brutal-btn-ghost w-full text-xs py-2"
                >
                    Got it
                </button>
            </div>
        </div>
    );
}
